import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { JAT_CHARACTERISTICS, JAT_DIFF_HELP } from "@/lib/jateamento";
import { maskDocument, maskPhone, maskCep, docKey } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Loader2, Send, Paperclip, X, CheckCircle2, FileText, Check, Plus, Pencil, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/pages/portal/Dashboard";

const SERVICOS = [
  "Centro de Usinagem CNC", "Torno CNC", "Fresamento", "Tornearia",
  "Corte Plasma CNC", "Corte Laser CNC", "Dobra de Chapas", "Jateamento",
  "Soldas (MMA / TIG / MIG / LASER)", "Gravação a Laser (Fiber / Diodo)",
  "Projetos Especiais",
];
const JAT_SERVICE = "Jateamento";
const DOBRA_SERVICE = "Dobra de Chapas";
const GRAV_SERVICE = "Gravação a Laser (Fiber / Diodo)";
const USINAGEM_SERVICES = new Set(["Centro de Usinagem CNC", "Torno CNC", "Fresamento", "Tornearia", "Torneamento"]);
const USI_FORMATS = [
  ["cilindrica", "Cilíndrica"],
  ["quadrada", "Quadrada"],
  ["retangular", "Retangular"],
];
const SHEET_SVC_FLAGS = { "Corte Plasma CNC": "plasma", "Corte Laser CNC": "laser" };
const GRADE_PADRAO = "__padrao__";
const ACCEPT = ".dxf,.dwg,.step,.stp,.pdf,.jpg,.jpeg,.png";
const MAX_MB = 50;
const STEPS = ["Cliente", "Serviço", "Dados", "Adicionar", "Revisão"];
const DRAFT_KEY = "alpha_orcamento_draft_v1";

const PAYMENT_CONDITIONS = [
  "Dinheiro, Pix / Transferência Bancária",
  "Boleto 5 dias", "Boleto 15 dias", "Boleto 30 dias", "Boleto 45 dias",
  "Boleto 60 dias", "Boleto 75 dias", "Boleto 90 dias",
];

const DEFAULT_OBS =
  "Valores referentes ao corte conforme desenho enviado pelo cliente.\n" +
  "* Material conforme especificado neste orçamento.\n" +
  "* Tolerâncias dimensionais conforme capacidade do processo de corte a plasma (Tol. Laser: +- 0,3 mm / Tol. Oxicorte / Plasma: +- 3 mm / Tol. Espessura da chapa: +- 3% de variação)\n" +
  "* Não estão inclusos serviços de dobra, solda, pintura, usinagem ou acabamento, salvo quando expressamente descritos.";

const INITIAL_JAT = { length_mm: "", width_mm: "", height_mm: "", weight_kg: "",
                      quantity: "", faces: "", difficulty: "",
                      piece_type: "", part_material: "", part_grade: "", part_thickness_mm: "",
                      characteristics: [], characteristics_other: "", notes: "" };

const INITIAL_CLIENT = {
  person_type: "PJ",
  client_name: "", client_document: "", client_email: "", client_phone: "",
  trade_name: "", whatsapp: "", address: "",
  address_number: "", complement: "", neighborhood: "", cep: "",
  city: "", state: "",
};

const INITIAL_DOBRA = { material: "", thickness_mm: "", bend_length_mm: "", bend_count: "" };

const INITIAL_GRAV = { material: "", quantity: "1", width_mm: "", height_mm: "", depth: "", notes: "", files: [] };
const INITIAL_GRAV_FORM = { machine: "", all_equal: "sim", gravs: [{ ...INITIAL_GRAV }] };
const INITIAL_USI = { material: "", format: "", dim_a: "", dim_b: "", dim_c: "", tolerance: "", as_drawing: false };

const INITIAL_ITEM = {
  service: "", part_name: "", quantity: "",
  material: "", material_grade: "", thickness_mm: "",
  heat_treatment: "", raw_dimensions: "", finish: "", tolerances: "",
  notes: "",
  files: [], analysis: null, calcResult: null,
  jateamento: { ...INITIAL_JAT },
  dobra: { ...INITIAL_DOBRA },
  gravacao: null,               // item pronto (1 gravação = 1 item)
  gravForm: { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] },  // formulário multi-gravação
  usinagem: { ...INITIAL_USI },
};

const INITIAL_EXTRAS = {
  deadline: "", urgency: "Normal", general_notes: "",
};

// Prazo desejado padrão: data do orçamento + 7 dias corridos (calculado na hora, nunca fixo)
const todayPlus7 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const inputCls =
  "mt-1.5 w-full bg-zinc-900/70 border border-zinc-700 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-zinc-600 disabled:opacity-50";
const labelCls = "text-xs uppercase tracking-wider text-zinc-400";

export default function OrcamentoNovo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [client, setClient] = useState({ ...INITIAL_CLIENT });
  const [items, setItems] = useState([]);
  const [item, setItem] = useState({ ...INITIAL_ITEM, jateamento: { ...INITIAL_JAT }, dobra: { ...INITIAL_DOBRA }, gravForm: { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] } });
  const [editIdx, setEditIdx] = useState(null);
  const [extras, setExtras] = useState(() => ({ ...INITIAL_EXTRAS, deadline: todayPlus7() }));
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [done, setDone] = useState(null);
  const [knownCustomer, setKnownCustomer] = useState(false);
  const [servicesList, setServicesList] = useState(SERVICOS);
  const [jatCfg, setJatCfg] = useState(null);
  const [jatHelp, setJatHelp] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [dobraCfg, setDobraCfg] = useState(null);
  const [dobraPrices, setDobraPrices] = useState([]);
  const [gravPrices, setGravPrices] = useState([]);
  const [usiMaterials, setUsiMaterials] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/services").then(({ data }) => {
      if (Array.isArray(data) && data.length) setServicesList(data);
    }).catch(() => {});
    api.get("/jateamento-config/public").then(({ data }) => setJatCfg(data)).catch(() => {});
    api.get("/sheet-configs/public").then(({ data }) => setCatalog(Array.isArray(data) ? data : [])).catch(() => setCatalog([]));
    api.get("/dobra-config/public").then(({ data }) => setDobraCfg(data)).catch(() => {});
    api.get("/dobra-prices/public").then(({ data }) => setDobraPrices(Array.isArray(data) ? data : [])).catch(() => setDobraPrices([]));
    api.get("/gravacao-prices/public").then(({ data }) => setGravPrices(Array.isArray(data) ? data : [])).catch(() => setGravPrices([]));
    api.get("/materials/public").then(({ data }) => setUsiMaterials(Array.isArray(data) ? data : [])).catch(() => setUsiMaterials([]));
  }, []);

  // ---- Rascunho local (§21): sai da tela e continua depois. Arquivos precisam ser reanexados. ----
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && (d.items?.length || d.client?.client_name)) {
        setClient({ ...INITIAL_CLIENT, ...(d.client || {}) });
        setItems((d.items || []).map((it) => ({
          ...INITIAL_ITEM, ...it, files: [],
          jateamento: { ...INITIAL_JAT, ...(it.jateamento || {}) },
          dobra: { ...INITIAL_DOBRA, ...(it.dobra || {}) },
          gravForm: it.gravForm
            ? { ...INITIAL_GRAV_FORM, ...it.gravForm, gravs: (it.gravForm.gravs || []).map((g) => ({ ...INITIAL_GRAV, ...g, files: [] })) }
            : { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] },
          usinagem: { ...INITIAL_USI, ...(it.usinagem || {}) },
        })));
        setExtras({ ...INITIAL_EXTRAS, ...(d.extras || {}) });
        setStep(d.step ?? 0);
        toast.info("Rascunho restaurado. Reanexe os arquivos das peças antes de enviar.");
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (done) return;
    try {
      const serializable = items.map(({ files, ...it }) => it);
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ client, items: serializable, extras, step }));
    } catch {}
  }, [client, items, extras, step, done]);

  const setC = (k) => (e) => setClient((f) => ({ ...f, [k]: e.target.value }));
  const setCPhone = (k) => (e) => setClient((f) => ({ ...f, [k]: maskPhone(e.target.value) }));
  const setI = (k) => (e) => setItem((f) => ({ ...f, [k]: e.target.value }));
  const setJat = (k) => (e) => setItem((f) => ({ ...f, jateamento: { ...f.jateamento, [k]: e.target.value } }));
  const setGravForm = (patch) => setItem((f) => ({ ...f, gravForm: { ...f.gravForm, ...patch } }));
  const setGravEntry = (gi, patch) =>
    setItem((f) => ({ ...f, gravForm: { ...f.gravForm, gravs: f.gravForm.gravs.map((g, i) => (i === gi ? { ...g, ...patch } : g)) } }));
  const setUsi = (k) => (e) => setItem((f) => ({ ...f, usinagem: { ...f.usinagem, [k]: e.target.value } }));
  const addGravFiles = (gi, list) => {
    const ok = [];
    for (const f of list) {
      const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
      if (!ACCEPT.includes(`.${ext}`)) { toast.error(`Tipo não permitido: ${f.name}`); continue; }
      if (f.size > MAX_MB * 1048576) { toast.error(`Arquivo acima de ${MAX_MB} MB: ${f.name}`); continue; }
      ok.push(f);
    }
    if (ok.length) setGravEntry(gi, { files: ok.slice(0, 1) });   // 1 arquivo/imagem por gravação
  };

  // ---- Cascata Material → Especificação → Espessura (catálogo Materiais/Preços, serviço do item) ----
  const flag = SHEET_SVC_FLAGS[item.service];
  const sheetRequired = !!flag;
  const catalogFit = (catalog || []).filter((c) => !flag || c.services?.[flag]);
  const materialOptions = [...new Set(catalogFit.map((c) => c.material))].sort();
  const gradeOptions = [...new Set(catalogFit.filter((c) => c.material === item.material).map((c) => c.grade || GRADE_PADRAO))].sort();
  const thicknessOptions = catalogFit
    .filter((c) => c.material === item.material && (c.grade || GRADE_PADRAO) === item.material_grade)
    .map((c) => Number(c.thickness_mm)).sort((a, b) => a - b);

  const isJat = item.service === JAT_SERVICE;
  const isDobra = item.service === DOBRA_SERVICE;
  const isGrav = item.service === GRAV_SERVICE;
  const isUsi = USINAGEM_SERVICES.has(item.service);
  const gravMachineOptions = [...new Set(gravPrices.map((p) => p.machine))].sort();
  const gravMaterialOptions = (machine) =>
    [...new Set(gravPrices.filter((p) => p.machine === machine).map((p) => p.material))].sort();
  const gravDepthOptions = (machine, material) =>
    [...new Set(gravPrices.filter((p) => p.machine === machine && p.material === material).map((p) => p.depth))].sort();
  const dobraMaterialOptions = [...new Set(dobraPrices.map((p) => p.material))].sort();
  const dobraThicknessOptions = dobraPrices
    .filter((p) => p.material === item.dobra.material)
    .map((p) => Number(p.thickness_mm)).sort((a, b) => a - b);
  const dobraLenMax = Number(dobraCfg?.max_bend_length_mm) || 0;
  const dobraLenExceeded = isDobra && dobraLenMax > 0 && Number(item.dobra.bend_length_mm) > dobraLenMax;
  const jatDifficulties = jatCfg?.difficulties?.length
    ? jatCfg.difficulties
    : [{ key: "baixa", label: "Baixa" }, { key: "media", label: "Média" }, { key: "alta", label: "Alta" }, { key: "muito_alta", label: "Muito alta" }];
  const jatCatalog = (catalog || []).filter((c) => c.services?.jateamento);
  const jatMaterialOptions = [...new Set(jatCatalog.map((c) => c.material))].sort();
  const jatGradeOptions = [...new Set(jatCatalog.filter((c) => c.material === item.jateamento.part_material).map((c) => c.grade || GRADE_PADRAO))].sort();
  const jatThicknessOptions = jatCatalog
    .filter((c) => c.material === item.jateamento.part_material && (c.grade || GRADE_PADRAO) === item.jateamento.part_grade)
    .map((c) => Number(c.thickness_mm)).sort((a, b) => a - b);
  const jatExceeded = [];
  if (isJat && jatCfg?.booth) {
    const b = jatCfg.booth;
    for (const [label, val, lim, unit] of [
      ["Comprimento", Number(item.jateamento.length_mm), b.max_length_mm, "mm"],
      ["Largura", Number(item.jateamento.width_mm), b.max_width_mm, "mm"],
      ["Altura", Number(item.jateamento.height_mm), b.max_height_mm, "mm"],
      ["Peso", Number(item.jateamento.weight_kg), b.max_weight_kg, "kg"],
    ]) {
      if (lim > 0 && val > lim) jatExceeded.push({ label, val, lim, unit });
    }
  }

  const lookupDoc = async () => {
    const digits = docKey(client.client_document);
    if (![11, 14].includes(digits.length)) return;
    try {
      const { data } = await api.get(`/customers/lookup/${digits}`);
      if (data.exists) {
        setKnownCustomer(true);
        setClient((f) => ({
          ...f,
          person_type: data.person_type || f.person_type,
          client_name: data.name || f.client_name,
          trade_name: data.trade_name ?? f.trade_name,
          client_email: data.email || f.client_email,
          client_phone: data.phone || f.client_phone,
          whatsapp: data.whatsapp ?? f.whatsapp,
          address: data.address ?? f.address,
          address_number: data.address_number ?? f.address_number,
          complement: data.complement ?? f.complement,
          neighborhood: data.neighborhood ?? f.neighborhood,
          cep: data.cep ?? f.cep,
          city: data.city || f.city,
          state: data.state || f.state,
        }));
        toast.info("Cadastro localizado! Dados preenchidos — o orçamento será vinculado ao seu histórico.");
      } else {
        setKnownCustomer(false);
      }
    } catch {}
  };

  const clientValid = () => {
    const docLen = docKey(client.client_document).length;
    return client.client_name.trim() && [11, 14].includes(docLen) && /\S+@\S+\.\S+/.test(client.client_email)
      && client.client_phone.trim() && client.city.trim() && client.state.trim();
  };

  const gravEntryError = (g) => {
    if (!g.material) return "Selecione o material de gravação.";
    if (!(Number(g.width_mm) > 0) || !(Number(g.height_mm) > 0)) return "Informe largura e altura da gravação (mm) maiores que zero.";
    if (Number(g.width_mm) > 100 || Number(g.height_mm) > 100) return "O tamanho máximo permitido para uma gravação é 100 × 100 mm.";
    if (!g.depth) return "Selecione a profundidade da gravação.";
    if (!(Number.isInteger(Number(g.quantity)) && Number(g.quantity) >= 1)) return "Quantidade de gravações deve ser um número inteiro maior que zero.";
    if (!g.files.length) return "Anexe o arquivo/imagem da gravação.";
    return null;
  };

  const itemValid = () => {
    if (!item.service) return false;
    if (isGrav) {
      const gf = item.gravForm;
      return !!gf.machine && gf.gravs.length >= 1 && gf.gravs.every((g) => !gravEntryError(g));
    }
    if (isUsi) {
      const u = item.usinagem;
      const dimsOk = u.format === "retangular"
        ? Number(u.dim_a) > 0 && Number(u.dim_b) > 0 && Number(u.dim_c) > 0
        : !!u.format && Number(u.dim_a) > 0 && Number(u.dim_b) > 0;
      return !!item.part_name.trim() && Number(item.quantity) >= 1 && !!u.material && dimsOk;
    }
    if (isDobra) {
      const db = item.dobra;
      return !!item.part_name.trim() && Number(item.quantity) >= 1 && !!db.material
        && Number(db.thickness_mm) > 0 && Number(db.bend_length_mm) > 0 && Number(db.bend_count) >= 1;
    }
    if (isJat) {
      const j = item.jateamento;
      const baseOk = Number(j.length_mm) > 0 && Number(j.width_mm) > 0 && Number(j.height_mm) > 0
        && Number(j.weight_kg) > 0 && Number(j.quantity) >= 1
        && Number(j.faces) >= 1 && Number(j.faces) <= 10 && !!j.difficulty;
      if (!baseOk) return false;
      if (j.part_material) {
        if (jatGradeOptions.length && !j.part_grade) return false;
        if (!(Number(j.part_thickness_mm) > 0)) return false;
      }
      return true;
    }
    if (!item.part_name.trim() || !(Number(item.quantity) >= 1)) return false;
    if (sheetRequired) {
      if (!item.material) return false;
      if (gradeOptions.length && !item.material_grade) return false;
      if (!(Number(item.thickness_mm) > 0)) return false;
    }
    return true;
  };

  const itemError = () => {
    if (isUsi) return "Preencha nome da peça, quantidade, material, formato e dimensões da matéria-prima.";
    if (isGrav) {
      if (!item.gravForm.machine) return "Selecione a máquina de gravação.";
      const bad = item.gravForm.gravs.find((g) => gravEntryError(g));
      return bad ? gravEntryError(bad) : "Preencha os campos obrigatórios da gravação.";
    }
    if (isDobra) return "Preencha peça, quantidade, material, espessura, comprimento da dobra e quantidade de dobras.";
    if (isJat) return "Preencha dimensões, peso, quantidade, faces e dificuldade do jateamento.";
    if (!item.part_name.trim()) return "Informe o nome ou identificação da peça.";
    if (!(Number(item.quantity) >= 1)) return "Informe a quantidade.";
    if (sheetRequired) {
      if (!item.material) return "Selecione o material.";
      if (gradeOptions.length && !item.material_grade) return "Selecione a especificação/liga.";
      if (!(Number(item.thickness_mm) > 0)) return "Selecione a espessura.";
    }
    return "Preencha os campos obrigatórios (*) para continuar.";
  };

  // Valida um item JÁ ADICIONADO (inclui itens restaurados do rascunho local) antes do envio
  const itemIssue = (it) => {
    if (USINAGEM_SERVICES.has(it.service)) {
      const u = it.usinagem || {};
      const missing = [];
      if (!(it.part_name || "").trim()) missing.push("nome da peça");
      if (!(Number(it.quantity) >= 1)) missing.push("quantidade");
      if (!u.material) missing.push("material");
      if (!u.format) missing.push("formato da matéria-prima");
      if (u.format === "retangular" ? !(Number(u.dim_a) > 0 && Number(u.dim_b) > 0 && Number(u.dim_c) > 0)
                                    : !(Number(u.dim_a) > 0 && Number(u.dim_b) > 0)) missing.push("dimensões da matéria-prima");
      return missing.length ? `${it.service} — informe: ${missing.join(", ")}.` : null;
    }
    if (it.service === JAT_SERVICE) {
      const j = it.jateamento || {};
      const missing = [];
      if (!(Number(j.length_mm) > 0) || !(Number(j.width_mm) > 0) || !(Number(j.height_mm) > 0)) missing.push("dimensões");
      if (!(Number(j.weight_kg) > 0)) missing.push("peso");
      if (!(Number(j.quantity) >= 1)) missing.push("quantidade");
      if (!(Number(j.faces) >= 1)) missing.push("faces");
      if (!j.difficulty) missing.push("dificuldade");
      return missing.length ? `Jateamento — informe: ${missing.join(", ")}.` : null;
    }
    if (it.service === DOBRA_SERVICE) {
      const db = it.dobra || {};
      const missing = [];
      if (!db.material) missing.push("material");
      if (!(Number(db.thickness_mm) > 0)) missing.push("espessura");
      if (!(Number(db.bend_length_mm) > 0)) missing.push("comprimento da dobra");
      if (!(Number(db.bend_count) >= 1)) missing.push("quantidade de dobras");
      return missing.length ? `Dobra de Chapas — informe: ${missing.join(", ")}.` : null;
    }
    if (it.service === GRAV_SERVICE) {
      const g = it.gravacao || {};
      const missing = [];
      if (!g.machine) missing.push("máquina");
      if (!g.material) missing.push("material");
      if (!(Number(g.width_mm) > 0) || !(Number(g.height_mm) > 0)) missing.push("tamanho");
      if (Number(g.width_mm) > 100 || Number(g.height_mm) > 100) return "Gravação a Laser — o tamanho máximo permitido é 100 × 100 mm.";
      if (!g.depth) missing.push("profundidade");
      return missing.length ? `Gravação a Laser — informe: ${missing.join(", ")}.` : null;
    }
    if (SHEET_SVC_FLAGS[it.service] && (!it.material || !(Number(it.thickness_mm) > 0))) {
      return `${it.service} — selecione material, especificação e espessura.`;
    }
    return null;
  };

  const addItemFiles = async (list) => {
    const ok = [];
    for (const f of list) {
      const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
      if (!ACCEPT.includes(`.${ext}`)) { toast.error(`Tipo não permitido: ${f.name}`); continue; }
      if (f.size > MAX_MB * 1048576) { toast.error(`Arquivo acima de ${MAX_MB} MB: ${f.name}`); continue; }
      ok.push(f);
    }
    if (!ok.length) return;
    setItem((it) => ({ ...it, files: [...it.files.filter((x) => !ok.some((f) => f.name === x.name)), ...ok] }));
    const dxf = ok.find((f) => f.name.toLowerCase().endsWith(".dxf"));
    if (dxf) {
      setAnalyzing(true);
      try {
        const fd = new FormData();
        fd.append("file", dxf);
        const { data } = await api.post("/dxf/analyze", fd);
        setItem((it) => ({ ...it, analysis: data, calcResult: null }));
        toast.success(`DXF lido: ${Number(data.cut_length_m).toFixed(4)} m de corte, ${data.pierce_count ?? 0} entrada(s), ${Number(data.area_m2).toFixed(6)} m².`);
      } catch (err) {
        toast.error(`Não foi possível ler o DXF: ${formatApiError(err)}`);
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const normalizedJat = (j) => ({
    length_mm: Number(j.length_mm), width_mm: Number(j.width_mm), height_mm: Number(j.height_mm),
    weight_kg: Number(j.weight_kg), quantity: Math.max(1, parseInt(j.quantity, 10) || 1),
    faces: parseInt(j.faces, 10), difficulty: j.difficulty,
    piece_type: j.piece_type, part_material: j.part_material,
    part_grade: j.part_grade === GRADE_PADRAO ? "" : j.part_grade,
    part_thickness_mm: Number(j.part_thickness_mm) || 0,
    characteristics: j.characteristics, characteristics_other: j.characteristics_other,
    notes: j.notes,
  });

  const itemMetrics = (it) => {
    if (it.analysis) {
      return {
        area_m2: it.analysis.area_m2,
        cut_meters: it.analysis.cut_length_m,
        pierces: it.analysis.pierce_count ?? it.analysis.holes_count ?? 0,
      };
    }
    return null;
  };

  // ---- AVANÇAR: valida no servidor (sem expor valores) → adiciona o serviço como item individual ----
  const advanceItem = async () => {
    if (!itemValid()) {
      toast.error(itemError());
      return;
    }
    // Gravação a Laser: cada gravação vira um item do orçamento (validação no servidor, sem valores)
    if (isGrav) {
      setCalculating(true);
      try {
        for (const g of item.gravForm.gravs) {
          await api.post("/public/calculate-item", {
            service: item.service,
            quantity: parseInt(g.quantity, 10) || 1,
            gravacao: {
              machine: item.gravForm.machine, material: g.material, depth: g.depth,
              width_mm: Number(g.width_mm), height_mm: Number(g.height_mm),
              quantity: parseInt(g.quantity, 10) || 1, notes: g.notes || "",
            },
          });
        }
      } catch (err) {
        toast.error(formatApiError(err));
        setCalculating(false);
        return;
      }
      setCalculating(false);
      const mkItem = (g) => ({
        ...INITIAL_ITEM,
        jateamento: { ...INITIAL_JAT },
        dobra: { ...INITIAL_DOBRA },
        gravForm: { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] },
        service: item.service,
        part_name: `Gravação ${g.material} ${g.width_mm}×${g.height_mm} mm`,
        material: g.material,
        quantity: String(parseInt(g.quantity, 10) || 1),
        files: g.files,
        gravacao: {
          machine: item.gravForm.machine, material: g.material, depth: g.depth,
          width_mm: Number(g.width_mm), height_mm: Number(g.height_mm),
          quantity: parseInt(g.quantity, 10) || 1, notes: g.notes || "",
        },
        techFlag: false,
      });
      if (editIdx != null) {
        setItems((prev) => prev.map((x, i) => (i === editIdx ? mkItem(item.gravForm.gravs[0]) : x)));
      } else {
        setItems((prev) => [...prev, ...item.gravForm.gravs.map(mkItem)]);
      }
      setEditIdx(null);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // §24/§28: valores não são calculados nem exibidos no público — só após a conferência do orçamentista
    let techFlag = (isJat && jatExceeded.length > 0) || (isDobra && dobraLenExceeded);
    if (isJat || isDobra || sheetRequired) {
      setCalculating(true);
      try {
        const payload = isJat
          ? { service: item.service, jateamento: normalizedJat(item.jateamento) }
          : isDobra
            ? {
                service: item.service,
                quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
                dobra: {
                  material: item.dobra.material,
                  thickness_mm: Number(item.dobra.thickness_mm),
                  bend_length_mm: Number(item.dobra.bend_length_mm),
                  bend_count: Math.max(1, parseInt(item.dobra.bend_count, 10) || 1),
                  quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
                },
              }
            : {
                service: item.service,
                material: item.material,
                material_grade: item.material_grade === GRADE_PADRAO ? "" : item.material_grade,
                thickness_mm: Number(item.thickness_mm),
                quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
                metrics: itemMetrics(item),
              };
        const { data } = await api.post("/public/calculate-item", payload);
        if (data.tech_status === "Necessita análise técnica") {
          techFlag = true;
          toast.warning(isDobra
            ? "Dobra fora das regras automáticas — o orçamento seguirá para análise técnica."
            : "Peça fora da capacidade da cabine — o orçamento seguirá para análise técnica.");
        } else if (data.note) {
          toast.info(data.note);
        }
      } catch (err) {
        toast.error(formatApiError(err));
        setCalculating(false);
        return;
      }
      setCalculating(false);
    }
    const newItem = { ...item, calcResult: null, techFlag };
    setItems((prev) => (editIdx != null ? prev.map((x, i) => (i === editIdx ? newItem : x)) : [...prev, newItem]));
    setEditIdx(null);
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addAnother = () => {
    setItem({ ...INITIAL_ITEM, jateamento: { ...INITIAL_JAT }, dobra: { ...INITIAL_DOBRA }, gravForm: { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] }, usinagem: { ...INITIAL_USI } });
    setEditIdx(null);
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editItem = (idx) => {
    const it = items[idx];
    const gravForm = it.gravacao
      ? { machine: it.gravacao.machine || "", all_equal: "sim",
          gravs: [{ ...INITIAL_GRAV, material: it.gravacao.material || "",
                    quantity: String(it.gravacao.quantity || 1),
                    width_mm: it.gravacao.width_mm ?? "", height_mm: it.gravacao.height_mm ?? "",
                    depth: it.gravacao.depth || "", notes: it.gravacao.notes || "",
                    files: it.files || [] }] }
      : { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] };
    setItem({ ...it, gravForm });
    setEditIdx(idx);
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    toast.info("Serviço removido do orçamento.");
  };

  const next = () => {
    if (step === 0) {
      if (!clientValid()) {
        toast.error("Preencha nome, CPF/CNPJ, e-mail, telefone, cidade e estado.");
        return;
      }
      setStep(1);
    } else if (step === 1) {
      if (!item.service) {
        toast.error("Selecione o serviço.");
        return;
      }
      setStep(2);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    if (step === 2) { setStep(1); setEditIdx(null); }
    else if (step === 4) { setStep(3); }
    else if (step === 1) { setStep(items.length ? 3 : 0); }
    else { setStep((s) => Math.max(0, s - 1)); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Forma de pagamento é definida pela equipe administrativa após a conferência — o cliente não seleciona na solicitação.

  const submit = async () => {
    if (!clientValid()) {
      toast.error("Revise os dados do cliente (etapa 1).");
      return;
    }
    if (!items.length) {
      toast.error("Adicione ao menos um serviço ao orçamento.");
      return;
    }
    // Bloqueia envio com item incompleto (ex.: restaurado do rascunho) e abre o item para correção
    for (const [idx, it] of items.entries()) {
      const issue = itemIssue(it);
      if (issue) {
        toast.error(`Item ${idx + 1} (${it.part_name || it.service}): ${issue} Abrindo para correção…`);
        editItem(idx);
        return;
      }
    }
    setLoading(true);
    try {
      const firstSheet = items.find((i) => SHEET_SVC_FLAGS[i.service]);
      const { data: created } = await api.post("/quote-requests", {
        ...client,
        services: [...new Set(items.map((i) => i.service))],
        part_name: items[0]?.part_name || items[0]?.service || "",
        quantity: items.reduce((a, i) => a + (Number(i.quantity) || 1), 0),
        material: firstSheet?.material || "",
        material_grade: firstSheet ? (firstSheet.material_grade === GRADE_PADRAO ? "" : firstSheet.material_grade) : "",
        thickness_mm: firstSheet?.thickness_mm ? Number(firstSheet.thickness_mm) : "",
        deadline: extras.deadline,
        urgency: extras.urgency,
        general_notes: extras.general_notes,
        items: items.map((i) => ({
          service: i.service,
          part_name: i.part_name,
          quantity: Math.max(1, parseInt(i.quantity, 10) || 1),
          material: i.material,
          material_grade: i.material_grade === GRADE_PADRAO ? "" : i.material_grade,
          thickness_mm: i.thickness_mm ? Number(i.thickness_mm) : null,
          notes: i.notes,
          heat_treatment: i.heat_treatment,
          raw_dimensions: i.raw_dimensions,
          finish: i.finish,
          tolerances: i.tolerances,
          metrics: itemMetrics(i),
          jateamento: i.service === JAT_SERVICE ? normalizedJat(i.jateamento) : null,
          dobra: i.service === DOBRA_SERVICE
            ? {
                material: i.dobra.material,
                thickness_mm: Number(i.dobra.thickness_mm),
                bend_length_mm: Number(i.dobra.bend_length_mm),
                bend_count: Math.max(1, parseInt(i.dobra.bend_count, 10) || 1),
                quantity: Math.max(1, parseInt(i.quantity, 10) || 1),
              }
            : null,
          gravacao: i.service === GRAV_SERVICE ? i.gravacao : null,
          usinagem: USINAGEM_SERVICES.has(i.service)
            ? {
                material: i.usinagem.material, format: i.usinagem.format,
                dim_a: Number(i.usinagem.dim_a), dim_b: Number(i.usinagem.dim_b),
                dim_c: i.usinagem.format === "retangular" ? Number(i.usinagem.dim_c) : null,
                tolerance: i.usinagem.as_drawing ? "Conforme desenho" : (i.usinagem.tolerance || "").trim(),
                quantity: Math.max(1, parseInt(i.quantity, 10) || 1),
              }
            : null,
        })),
      });
      let uploaded = 0;
      for (const [idx, it] of items.entries()) {
        for (const f of it.files) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            const params = new URLSearchParams();
            if (!user && created.tracking_code) params.set("code", created.tracking_code);
            params.set("item", String(idx + 1));
            await api.post(`/quote-requests/${created.id}/files?${params.toString()}`, fd);
            uploaded += 1;
          } catch (err) {
            toast.error(`${f.name}: ${formatApiError(err)}`);
          }
        }
      }
      localStorage.removeItem(DRAFT_KEY);
      setDone({ ...created, filesCount: uploaded });
      toast.success(`Solicitação ${created.number} enviada para orçamento!`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-10" data-testid="quote-success-panel">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" strokeWidth={1.25} />
        <h1 className="display mt-5 text-3xl sm:text-4xl font-extrabold uppercase">Solicitação enviada!</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Recebemos sua solicitação e nossa equipe iniciará a análise. Retornaremos em até 48h úteis.
        </p>
        <div className="mt-8 card-tactical rounded-md p-7">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Protocolo do orçamento</div>
          <div className="mono mt-2 text-3xl font-bold text-orange-500" data-testid="quote-success-number">{done.number}</div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-zinc-400">
            {(done.services?.length ? done.services : [done.service_type]).map((s) => (
              <span key={s} className="border border-zinc-700 rounded-sm px-2 py-0.5 text-xs">{s}</span>
            ))}
            <StatusBadge status={done.status} />
          </div>
          {!!done.filesCount && <div className="mt-4 text-xs text-zinc-500">{done.filesCount} arquivo(s) anexado(s)</div>}
          {!user && (
            <div className="mt-5 rounded-sm border border-zinc-800 bg-zinc-900/50 p-4 text-left" data-testid="quote-success-track-note">
              <p className="text-xs text-zinc-400 leading-relaxed">
                Acompanhe sem login usando o <b className="text-zinc-200">protocolo</b> e o{" "}
                <b className="text-zinc-200">e-mail ou telefone</b> informados nesta solicitação.
                Você também receberá atualizações por e-mail a cada mudança de status.
              </p>
            </div>
          )}
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            data-testid="quote-success-view-list-button"
            onClick={() => navigate(user ? "/painel/orcamentos" : `/acompanhar?n=${done.number}`)}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
          >
            {user ? "Ver meus orçamentos" : "Acompanhar orçamento"}
          </button>
          <button
            data-testid="quote-success-new-button"
            onClick={() => {
              setDone(null); setItems([]); setStep(0);
              setClient({ ...INITIAL_CLIENT, client_email: user?.email || "" });
              setExtras({ ...INITIAL_EXTRAS });
            }}
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-orange-500 hover:text-orange-400 text-zinc-300 text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
          >
            Nova solicitação
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="portal-new-quote-page" className="max-w-3xl">
      <Link to={user ? "/painel/orcamentos" : "/"} data-testid="portal-new-quote-back-link"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500 hover:text-orange-400 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> {user ? "Meus orçamentos" : "Voltar ao site"}
      </Link>

      <h1 className="display mt-3 text-3xl sm:text-4xl font-extrabold uppercase">Novo Orçamento</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Um serviço por vez: selecione, preencha, avance — depois adicione outro ou envie. Seu progresso fica salvo como rascunho neste navegador.
      </p>

      {/* STEPPER */}
      <div className="mt-7 flex items-center" data-testid="quote-step-indicator">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2" data-testid={`quote-step-tab-${i}`}>
              <span className={`mono w-8 h-8 grid place-content-center rounded-sm border text-sm font-semibold transition-colors ${
                i < step ? "bg-orange-600 border-orange-600 text-white"
                : i === step ? "border-orange-500 text-orange-500"
                : "border-zinc-700 text-zinc-600"
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </span>
              <span className={`hidden sm:block text-[11px] font-semibold uppercase tracking-wider ${i === step ? "text-zinc-100" : "text-zinc-600"}`}>
                {s}
              </span>
            </div>
            {i < STEPS.length - 1 && <span className={`flex-1 h-px mx-2 ${i < step ? "bg-orange-600" : "bg-zinc-800"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-6 card-tactical rounded-md p-6 sm:p-7" data-testid="portal-new-quote-form">
        {/* ETAPA 1 — CLIENTE */}
        {step === 0 && (
          <section data-testid="quote-step-cliente">
            <h2 className="display text-xl font-bold uppercase tracking-wide flex items-center gap-2">
              <span className="mono text-orange-500 text-sm">01</span> Identificação do Cliente
            </h2>

            <div className="mt-5 grid grid-cols-2 gap-2.5 max-w-sm" data-testid="person-type-toggle">
              {[["PJ", "Pessoa Jurídica"], ["PF", "Pessoa Física"]].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`person-type-${v.toLowerCase()}`}
                  onClick={() => setClient({ ...client, person_type: v })}
                  className={`rounded-sm border px-3 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    client.person_type === v
                      ? "border-orange-500 bg-orange-500/10 text-orange-300"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="mt-5">
              <label className={labelCls}>{client.person_type === "PF" ? "CPF *" : "CNPJ *"}</label>
              <input
                data-testid="client-document-input"
                value={client.client_document}
                onChange={(e) => setClient((f) => ({ ...f, client_document: maskDocument(e.target.value) }))}
                onBlur={lookupDoc}
                className={`${inputCls} mono`}
                placeholder={client.person_type === "PF" ? "000.000.000-00" : "00.000.000/0000-00"}
              />
              <p className="mt-1 text-[11px] text-zinc-600">
                Seu CPF/CNPJ é seu cadastro — todos os orçamentos ficam vinculados ao seu histórico, sem precisar de senha.
              </p>
            </div>

            {knownCustomer && (
              <div className="mt-4 rounded-sm border border-emerald-900/60 bg-emerald-500/5 p-3.5 text-xs text-emerald-300 leading-relaxed" data-testid="known-customer-banner">
                Cadastro localizado! Seus dados foram preenchidos automaticamente e este orçamento será vinculado ao seu histórico. Atualize qualquer dado abaixo se necessário.
              </div>
            )}

            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nome / Razão Social *</label>
                <input data-testid="client-name-input" autoComplete="off" value={client.client_name} onChange={setC("client_name")} className={inputCls} placeholder="Digite seu nome ou razão social" />
              </div>
              {client.person_type === "PJ" && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Nome Fantasia</label>
                  <input data-testid="client-trade-name-input" autoComplete="off" value={client.trade_name} onChange={setC("trade_name")} className={inputCls} placeholder="Opcional" />
                </div>
              )}
              <div>
                <label className={labelCls}>E-mail *</label>
                <input data-testid="client-email-input" type="email" autoComplete="off" value={client.client_email} onChange={setC("client_email")} className={inputCls} placeholder="seu@email.com" />
              </div>
              <div>
                <label className={labelCls}>Telefone *</label>
                <input data-testid="client-phone-input" autoComplete="off" inputMode="tel" value={client.client_phone} onChange={setCPhone("client_phone")} className={inputCls} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <label className={labelCls}>WhatsApp</label>
                <input data-testid="client-whatsapp-input" autoComplete="off" inputMode="tel" value={client.whatsapp} onChange={setCPhone("whatsapp")} className={inputCls} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <label className={labelCls}>CEP</label>
                <input data-testid="client-cep-input" autoComplete="off" inputMode="numeric" value={client.cep}
                  onChange={(e) => setClient((f) => ({ ...f, cep: maskCep(e.target.value) }))} className={`${inputCls} mono`} placeholder="00000-000" />
              </div>
              <div>
                <label className={labelCls}>Endereço</label>
                <input data-testid="client-address-input" autoComplete="off" value={client.address} onChange={setC("address")} className={inputCls} placeholder="Rua / avenida" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Número</label>
                  <input data-testid="client-address-number-input" autoComplete="off" inputMode="numeric" value={client.address_number} onChange={setC("address_number")} className={inputCls} placeholder="Nº" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Complemento</label>
                  <input data-testid="client-complement-input" autoComplete="off" value={client.complement} onChange={setC("complement")} className={inputCls} placeholder="Opcional" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Bairro</label>
                <input data-testid="client-neighborhood-input" autoComplete="off" value={client.neighborhood} onChange={setC("neighborhood")} className={inputCls} placeholder="Bairro" />
              </div>
              <div className="grid grid-cols-3 gap-3 sm:col-span-2">
                <div className="col-span-2">
                  <label className={labelCls}>Cidade *</label>
                  <input data-testid="client-city-input" autoComplete="off" value={client.city} onChange={setC("city")} className={inputCls} placeholder="Sua cidade" />
                </div>
                <div>
                  <label className={labelCls}>Estado *</label>
                  <input data-testid="client-state-input" value={client.state} onChange={setC("state")} maxLength={2} className={`${inputCls} uppercase`} placeholder="PR" />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ETAPA 2 — SERVIÇO (um por vez) */}
        {step === 1 && (
          <section data-testid="quote-step-servico">
            <h2 className="display text-xl font-bold uppercase tracking-wide flex items-center gap-2">
              <span className="mono text-orange-500 text-sm">02</span> Serviço
            </h2>
            <p className="mt-1.5 text-xs text-zinc-500">
              Selecione <b className="text-zinc-300">um serviço</b> por vez. Depois de preencher os dados você poderá adicionar outro.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-2.5" data-testid="service-single-list">
              {servicesList.map((s) => {
                const active = item.service === s;
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`service-chip-${servicesList.indexOf(s)}`}
                    onClick={() => setItem({ ...INITIAL_ITEM, jateamento: { ...INITIAL_JAT }, dobra: { ...INITIAL_DOBRA }, gravForm: { ...INITIAL_GRAV_FORM, gravs: [{ ...INITIAL_GRAV }] }, usinagem: { ...INITIAL_USI }, service: s })}
                    className={`flex items-center gap-2.5 rounded-sm border px-3.5 py-3 text-left text-sm transition-colors ${
                      active
                        ? "border-orange-500 bg-orange-500/10 text-orange-300"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                    }`}
                  >
                    <span className={`w-4 h-4 shrink-0 rounded-full border grid place-content-center ${active ? "border-orange-500" : "border-zinc-600"}`}>
                      {active && <span className="w-2 h-2 rounded-full bg-orange-500" />}
                    </span>
                    {s === "Dobra de Chapas" && Number(dobraCfg?.max_thickness_mm) > 0
                      ? `Dobra de Chapas (até ${Number(dobraCfg.max_thickness_mm)} mm)`
                      : s}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ETAPA 3 — DADOS DO SERVIÇO */}
        {step === 2 && (
          <section data-testid="quote-step-peca">
            <h2 className="display text-xl font-bold uppercase tracking-wide flex items-center gap-2">
              <span className="mono text-orange-500 text-sm">03</span> Dados — {item.service}
            </h2>

            {isJat ? (
              <div className="mt-5 rounded-sm border border-orange-900/50 bg-orange-500/5 p-5" data-testid="jat-form">
                <h3 className="display text-sm font-bold uppercase tracking-wide text-orange-400">Dados do Jateamento</h3>
                <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">
                  A área é calculada automaticamente como <b className="text-zinc-300">ESTIMATIVA</b> a partir das dimensões informadas.
                  Fotos da peça (opcional, até 5) — envie por diferentes ângulos para um orçamento mais preciso.
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Comprimento (mm) *</label>
                    <input data-testid="jat-length" type="number" step="0.01" min="0" value={item.jateamento.length_mm} onChange={setJat("length_mm")} className={inputCls} placeholder="0,00 mm" />
                  </div>
                  <div>
                    <label className={labelCls}>Largura (mm) *</label>
                    <input data-testid="jat-width" type="number" step="0.01" min="0" value={item.jateamento.width_mm} onChange={setJat("width_mm")} className={inputCls} placeholder="0,00 mm" />
                  </div>
                  <div>
                    <label className={labelCls}>Altura (mm) *</label>
                    <input data-testid="jat-height" type="number" step="0.01" min="0" value={item.jateamento.height_mm} onChange={setJat("height_mm")} className={inputCls} placeholder="0,00 mm" />
                  </div>
                  <div>
                    <label className={labelCls}>Peso aproximado (kg) *</label>
                    <input data-testid="jat-weight" type="number" step="0.01" min="0" value={item.jateamento.weight_kg} onChange={setJat("weight_kg")} className={inputCls} placeholder="0,00 kg" />
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade de peças *</label>
                    <input data-testid="jat-quantity" type="number" min="1" value={item.jateamento.quantity} onChange={setJat("quantity")} className={inputCls} placeholder="0" />
                  </div>
                  <div>
                    <label className={labelCls}>Faces a jatear (1–10) *</label>
                    <select data-testid="jat-faces" value={item.jateamento.faces} onChange={setJat("faces")} className={inputCls}>
                      <option value="">Selecionar</option>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n} {n === 1 ? "face" : "faces"}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>
                      Complexidade da peça *{" "}
                      <button type="button" data-testid="jat-diff-help-button" onClick={() => setJatHelp(true)}
                        className="ml-1 inline-grid place-content-center w-4 h-4 rounded-full border border-orange-500/70 text-orange-400 text-[10px] font-bold align-middle hover:bg-orange-500/10 transition-colors"
                        title="Veja qual classificação sua peça pertence">?</button>
                    </label>
                    <select data-testid="jat-difficulty" value={item.jateamento.difficulty} onChange={setJat("difficulty")} className={inputCls}>
                      <option value="">Selecionar</option>
                      {jatDifficulties.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Tipo de peça</label>
                    <input data-testid="jat-piece-type" value={item.jateamento.piece_type} onChange={setJat("piece_type")} className={inputCls} placeholder="Ex.: Suporte, chassi, tampa…" />
                  </div>
                  <div>
                    <label className={labelCls}>Material da peça</label>
                    <select data-testid="jat-part-material" value={item.jateamento.part_material}
                      onChange={(e) => setItem((f) => ({ ...f, jateamento: { ...f.jateamento, part_material: e.target.value, part_grade: "", part_thickness_mm: "" } }))} className={inputCls}>
                      <option value="">Selecionar (opcional)</option>
                      {jatMaterialOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  {item.jateamento.part_material && (
                    <>
                      <div>
                        <label className={labelCls}>Especificação — jateamento</label>
                        <select data-testid="jat-part-grade" value={item.jateamento.part_grade}
                          onChange={(e) => setItem((f) => ({ ...f, jateamento: { ...f.jateamento, part_grade: e.target.value, part_thickness_mm: "" } }))} className={inputCls}>
                          <option value="">Selecionar</option>
                          {jatGradeOptions.map((g) => <option key={g} value={g}>{g === GRADE_PADRAO ? "Padrão (sem especificação)" : g}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Espessura (mm) — jateamento</label>
                        <select data-testid="jat-part-thickness" value={item.jateamento.part_thickness_mm}
                          onChange={(e) => setItem((f) => ({ ...f, jateamento: { ...f.jateamento, part_thickness_mm: e.target.value } }))} className={inputCls}>
                          <option value="">Selecionar</option>
                          {jatThicknessOptions.map((t) => <option key={t} value={t}>{t} mm</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  <div className="col-span-2 sm:col-span-3">
                    <label className={labelCls}>O que torna a peça mais difícil de jatear? (opcional — marque quantas quiser)</label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="jat-characteristics">
                      {JAT_CHARACTERISTICS.map((c) => (
                        <label key={c.key} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                          <input type="checkbox" data-testid={`jat-char-${c.key}`}
                            checked={item.jateamento.characteristics.includes(c.key)}
                            onChange={() => setItem((f) => {
                              const cur = f.jateamento.characteristics;
                              return { ...f, jateamento: { ...f.jateamento, characteristics: cur.includes(c.key) ? cur.filter((x) => x !== c.key) : [...cur, c.key] } };
                            })}
                            className="accent-orange-500 w-4 h-4" />
                          {c.label}
                        </label>
                      ))}
                    </div>
                    {item.jateamento.characteristics.includes("outro") && (
                      <input data-testid="jat-char-other" value={item.jateamento.characteristics_other} onChange={setJat("characteristics_other")}
                        className={`${inputCls} mt-2`} placeholder="Descreva a outra característica…" />
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-3">
                    <label className={labelCls}>Observações do jateamento</label>
                    <textarea data-testid="jat-notes" rows={2} value={item.jateamento.notes} onChange={setJat("notes")} className={`${inputCls} resize-none`} placeholder="Geometria irregular, áreas que não devem ser jateadas, acabamento esperado…" />
                  </div>
                </div>
                {!!jatExceeded.length && (
                  <div className="mt-4 rounded-sm border border-red-900/60 bg-red-500/10 p-3.5" data-testid="jat-capacity-alert">
                    <div className="text-xs font-bold uppercase tracking-widest text-red-400">Peça fora da capacidade da cabine</div>
                    <ul className="mt-2 space-y-1 text-xs text-red-300">
                      {jatExceeded.map((e) => (
                        <li key={e.label} data-testid={`jat-exceeded-${e.label.toLowerCase()}`}>
                          {e.label} informado: <b>{e.val} {e.unit}</b> — Limite da cabine: <b>{e.lim} {e.unit}</b> ({e.label} acima da capacidade máxima da cabine)
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-red-300/80">
                      Status técnico: <b>Necessita análise técnica</b> — sua solicitação será salva e encaminhada para análise da equipe.
                    </p>
                  </div>
                )}
              </div>
            ) : isGrav ? (
              <div className="mt-5 rounded-sm border border-orange-900/50 bg-orange-500/5 p-5" data-testid="grav-form">
                <h3 className="display text-sm font-bold uppercase tracking-wide text-orange-400">Dados da Gravação a Laser</h3>
                <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">
                  Tamanho máximo por gravação: <b className="text-zinc-300">100 × 100 mm</b>. Anexe a imagem/arquivo de cada gravação para avaliação.
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Máquina de gravação *</label>
                    <select data-testid="grav-machine-select" value={item.gravForm.machine}
                      onChange={(e) => setGravForm({ machine: e.target.value, gravs: item.gravForm.gravs.map((g) => ({ ...g, material: "", depth: "" })) })}
                      className={inputCls}>
                      <option value="">Selecionar</option>
                      {gravMachineOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    {!gravMachineOptions.length && (
                      <p className="mt-1 text-[11px] text-amber-400" data-testid="grav-machine-empty-note">Nenhuma máquina de gravação disponível no momento. Entre em contato conosco.</p>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>As gravações são todas iguais? *</label>
                    <div className="mt-1.5 flex gap-2" data-testid="grav-equal-toggle">
                      {[["sim", "Sim"], ["nao", "Não"]].map(([v, lbl]) => (
                        <button key={v} type="button" data-testid={`grav-equal-${v}`}
                          onClick={() => setGravForm({ all_equal: v, gravs: v === "sim" ? [item.gravForm.gravs[0] || { ...INITIAL_GRAV }] : item.gravForm.gravs })}
                          className={`flex-1 rounded-sm border px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                            item.gravForm.all_equal === v ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {item.gravForm.gravs.map((g, gi) => (
                  <div key={gi} className="mt-4 rounded-sm border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`grav-entry-${gi}`}>
                    {item.gravForm.all_equal === "nao" && (
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Gravação {gi + 1}</span>
                        {item.gravForm.gravs.length > 1 && (
                          <button type="button" data-testid={`grav-remove-${gi}`}
                            onClick={() => setGravForm({ gravs: item.gravForm.gravs.filter((_, i) => i !== gi) })}
                            className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-400">
                            <Trash2 className="w-3 h-3 inline" /> Remover
                          </button>
                        )}
                      </div>
                    )}
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <label className={labelCls}>Material de gravação *</label>
                        <select data-testid={`grav-material-select-${gi}`} value={g.material} disabled={!item.gravForm.machine}
                          onChange={(e) => setGravEntry(gi, { material: e.target.value, depth: "" })} className={inputCls}>
                          <option value="">{item.gravForm.machine ? "Selecionar" : "Selecione a máquina primeiro"}</option>
                          {gravMaterialOptions(item.gravForm.machine).map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        {item.gravForm.machine && !gravMaterialOptions(item.gravForm.machine).length && (
                          <p className="mt-1 text-[11px] text-amber-400" data-testid={`grav-material-empty-${gi}`}>Não há materiais cadastrados para esta máquina. Entre em contato com a Alpha Metais.</p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>Quantidade de gravações *</label>
                        <input data-testid={`grav-quantity-${gi}`} type="number" min="1" step="1" autoComplete="off"
                          value={g.quantity} onChange={(e) => setGravEntry(gi, { quantity: e.target.value })}
                          className={inputCls} placeholder="Ex.: 10" />
                      </div>
                      <div>
                        <label className={labelCls}>Profundidade da gravação *</label>
                        <select data-testid={`grav-depth-select-${gi}`} value={g.depth} disabled={!g.material}
                          onChange={(e) => setGravEntry(gi, { depth: e.target.value })} className={inputCls}>
                          <option value="">{g.material ? "Selecionar" : "Selecione o material primeiro"}</option>
                          {gravDepthOptions(item.gravForm.machine, g.material).map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Largura da gravação (mm) *</label>
                        <input data-testid={`grav-width-${gi}`} type="number" step="0.1" min="0" max="100" autoComplete="off"
                          value={g.width_mm} onChange={(e) => setGravEntry(gi, { width_mm: e.target.value })}
                          className={inputCls} placeholder="máx. 100" />
                      </div>
                      <div>
                        <label className={labelCls}>Altura da gravação (mm) *</label>
                        <input data-testid={`grav-height-${gi}`} type="number" step="0.1" min="0" max="100" autoComplete="off"
                          value={g.height_mm} onChange={(e) => setGravEntry(gi, { height_mm: e.target.value })}
                          className={inputCls} placeholder="máx. 100" />
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <label className={labelCls}>Arquivo/imagem da gravação *</label>
                        <input type="file" accept={ACCEPT} className="hidden" id={`grav-file-input-${gi}`}
                          data-testid={`grav-file-input-${gi}`}
                          onChange={(e) => { addGravFiles(gi, Array.from(e.target.files || [])); e.target.value = ""; }} />
                        <button type="button" data-testid={`grav-file-button-${gi}`}
                          onClick={() => document.getElementById(`grav-file-input-${gi}`)?.click()}
                          className="mt-1.5 w-full border border-dashed border-zinc-700 hover:border-orange-500 rounded-sm py-2.5 text-xs font-medium uppercase tracking-wider text-zinc-500 hover:text-orange-400 transition-colors">
                          {g.files.length ? g.files[0].name : "Anexar arquivo"}
                        </button>
                      </div>
                      <div className="col-span-2 sm:col-span-3">
                        <label className={labelCls}>Observações</label>
                        <input data-testid={`grav-notes-${gi}`} value={g.notes} autoComplete="off"
                          onChange={(e) => setGravEntry(gi, { notes: e.target.value })}
                          className={inputCls} placeholder="Posição da gravação, referência, cor…" />
                      </div>
                    </div>
                    {(Number(g.width_mm) > 100 || Number(g.height_mm) > 100) && (
                      <p className="mt-2 text-[11px] font-semibold text-red-400" data-testid={`grav-size-error-${gi}`}>
                        O tamanho máximo permitido para uma gravação é 100 × 100 mm.
                      </p>
                    )}
                  </div>
                ))}

                {item.gravForm.all_equal === "nao" && (
                  <button type="button" data-testid="grav-add-button"
                    onClick={() => setGravForm({ gravs: [...item.gravForm.gravs, { ...INITIAL_GRAV }] })}
                    className="mt-4 w-full border border-dashed border-zinc-700 hover:border-orange-500 rounded-sm py-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-orange-400 transition-colors">
                    + Adicionar outra gravação
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Nome ou identificação da peça *</label>
                  <input data-testid="quote-part-name-input" autoComplete="off" value={item.part_name} onChange={setI("part_name")} className={inputCls} placeholder="Ex.: Flange de fixação Ø200" />
                </div>
                <div>
                  <label className={labelCls}>Quantidade *</label>
                  <input data-testid="quote-quantity-input" type="number" min="1" autoComplete="off" value={item.quantity} onChange={setI("quantity")} className={inputCls} placeholder="0" />
                </div>
                {isDobra ? (
                  <>
                    <div>
                      <label className={labelCls}>Material *</label>
                      <select data-testid="dobra-material-select" value={item.dobra.material}
                        onChange={(e) => setItem((f) => ({ ...f, dobra: { ...f.dobra, material: e.target.value, thickness_mm: "" } }))} className={inputCls}>
                        <option value="">Selecionar</option>
                        {dobraMaterialOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      {!dobraMaterialOptions.length && (
                        <p className="mt-1 text-[11px] text-amber-400" data-testid="dobra-material-empty-note">Nenhum material de dobra disponível para orçamento no momento. Entre em contato conosco.</p>
                      )}
                    </div>
                    <div>
                      <label className={labelCls}>Espessura (mm) *</label>
                      <select data-testid="dobra-thickness-select" value={item.dobra.thickness_mm} disabled={!item.dobra.material}
                        onChange={(e) => setItem((f) => ({ ...f, dobra: { ...f.dobra, thickness_mm: e.target.value } }))}
                        className={inputCls}>
                        <option value="">{item.dobra.material ? "Selecionar" : "Selecione o material primeiro"}</option>
                        {dobraThicknessOptions.map((t) => <option key={t} value={t}>{t} mm</option>)}
                      </select>
                      {item.dobra.material && !dobraThicknessOptions.length && (
                        <p className="mt-1 text-[11px] text-amber-400" data-testid="dobra-thickness-empty-note">
                          Não há espessuras cadastradas para este material. Entre em contato com a Alpha Metais.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className={labelCls}>Comprimento da dobra (mm) *</label>
                      <input data-testid="dobra-bend-length-input" type="number" step="0.01" min="0" autoComplete="off"
                        value={item.dobra.bend_length_mm}
                        onChange={(e) => setItem((f) => ({ ...f, dobra: { ...f.dobra, bend_length_mm: e.target.value } }))}
                        className={inputCls} placeholder="Ex.: 470" />
                    </div>
                    <div>
                      <label className={labelCls}>Quantidade de dobras *</label>
                      <input data-testid="dobra-bend-count-input" type="number" min="1" autoComplete="off"
                        value={item.dobra.bend_count}
                        onChange={(e) => setItem((f) => ({ ...f, dobra: { ...f.dobra, bend_count: e.target.value } }))}
                        className={inputCls} placeholder="Ex.: 4" />
                    </div>
                    {dobraLenExceeded && (
                      <div className="sm:col-span-2 rounded-sm border border-red-900/60 bg-red-500/10 p-3.5" data-testid="dobra-capacity-warning">
                        <div className="text-xs font-bold uppercase tracking-widest text-red-400">Comprimento acima da capacidade da dobradeira</div>
                        <p className="mt-1 text-xs text-red-300">Capacidade máxima: <b>{dobraLenMax} mm</b></p>
                        <p className="mt-1 text-[11px] text-red-300/80">
                          Status técnico: <b>Necessita análise técnica</b> — sua solicitação será salva e encaminhada para análise da equipe.
                        </p>
                      </div>
                    )}
                  </>
                ) : isUsi ? (
                  <>
                    <div>
                      <label className={labelCls}>Material *</label>
                      <select data-testid="usi-material-select" value={item.usinagem.material}
                        onChange={(e) => setItem((f) => ({ ...f, usinagem: { ...f.usinagem, material: e.target.value } }))} className={inputCls}>
                        <option value="">Selecionar</option>
                        {usiMaterials.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                      </select>
                      {!usiMaterials.length && (
                        <p className="mt-1 text-[11px] text-amber-400" data-testid="usi-material-empty-note">Nenhum material disponível para orçamento no momento. Entre em contato conosco.</p>
                      )}
                    </div>
                    <div>
                      <label className={labelCls}>Formato da matéria-prima *</label>
                      <select data-testid="usi-format-select" value={item.usinagem.format}
                        onChange={(e) => setItem((f) => ({ ...f, usinagem: { ...INITIAL_USI, material: f.usinagem.material, tolerance: f.usinagem.tolerance, as_drawing: f.usinagem.as_drawing, format: e.target.value } }))} className={inputCls}>
                        <option value="">Selecionar</option>
                        {USI_FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    {item.usinagem.format === "cilindrica" && (
                      <>
                        <div>
                          <label className={labelCls}>Diâmetro (mm) *</label>
                          <input data-testid="usi-dim-diameter" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_a} onChange={setUsi("dim_a")} className={inputCls} placeholder="Ex.: 50" />
                        </div>
                        <div>
                          <label className={labelCls}>Comprimento (mm) *</label>
                          <input data-testid="usi-dim-length" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_b} onChange={setUsi("dim_b")} className={inputCls} placeholder="Ex.: 300" />
                        </div>
                      </>
                    )}
                    {item.usinagem.format === "quadrada" && (
                      <>
                        <div>
                          <label className={labelCls}>Lado (mm) *</label>
                          <input data-testid="usi-dim-side" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_a} onChange={setUsi("dim_a")} className={inputCls} placeholder="Ex.: 40" />
                        </div>
                        <div>
                          <label className={labelCls}>Comprimento (mm) *</label>
                          <input data-testid="usi-dim-length" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_b} onChange={setUsi("dim_b")} className={inputCls} placeholder="Ex.: 300" />
                        </div>
                      </>
                    )}
                    {item.usinagem.format === "retangular" && (
                      <>
                        <div>
                          <label className={labelCls}>Largura (mm) *</label>
                          <input data-testid="usi-dim-width" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_a} onChange={setUsi("dim_a")} className={inputCls} placeholder="Ex.: 100" />
                        </div>
                        <div>
                          <label className={labelCls}>Espessura (mm) *</label>
                          <input data-testid="usi-dim-thickness" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_b} onChange={setUsi("dim_b")} className={inputCls} placeholder="Ex.: 20" />
                        </div>
                        <div>
                          <label className={labelCls}>Comprimento (mm) *</label>
                          <input data-testid="usi-dim-length" type="number" step="0.01" min="0" autoComplete="off"
                            value={item.usinagem.dim_c} onChange={setUsi("dim_c")} className={inputCls} placeholder="Ex.: 300" />
                        </div>
                      </>
                    )}
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Tolerância</label>
                      <input data-testid="usi-tolerance-input" value={item.usinagem.tolerance} autoComplete="off"
                        disabled={item.usinagem.as_drawing}
                        onChange={setUsi("tolerance")} className={inputCls} placeholder="Ex.: ± 0,02 mm" />
                      <label className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer">
                        <input type="checkbox" data-testid="usi-tolerance-drawing" checked={item.usinagem.as_drawing}
                          onChange={(e) => setItem((f) => ({ ...f, usinagem: { ...f.usinagem, as_drawing: e.target.checked } }))}
                          className="accent-orange-500 w-3.5 h-3.5" />
                        Conforme desenho
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                <div>
                  <label className={labelCls}>Material {sheetRequired ? "*" : ""}</label>
                  <select data-testid="quote-material-select" value={item.material}
                    onChange={(e) => setItem((f) => ({ ...f, material: e.target.value, material_grade: "", thickness_mm: "", calcResult: null }))} className={inputCls}>
                    <option value="">Selecionar</option>
                    {materialOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  {sheetRequired && !materialOptions.length && (
                    <p className="mt-1 text-[11px] text-amber-400" data-testid="material-empty-note">Este material/espessura não está disponível para orçamento no momento. Entre em contato conosco.</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Especificação do material {sheetRequired ? "*" : ""}</label>
                  <select data-testid="quote-material-grade-select" value={item.material_grade} disabled={!item.material}
                    onChange={(e) => setItem((f) => ({ ...f, material_grade: e.target.value, thickness_mm: "", calcResult: null }))} className={inputCls}>
                    <option value="">{item.material ? "Selecionar" : "Selecione o material primeiro"}</option>
                    {gradeOptions.map((g) => <option key={g} value={g}>{g === GRADE_PADRAO ? "Padrão (sem especificação)" : g}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Espessura (mm) {sheetRequired ? "*" : ""}</label>
                  <select data-testid="quote-thickness-select" value={item.thickness_mm} disabled={!item.material_grade}
                    onChange={(e) => setItem((f) => ({ ...f, thickness_mm: e.target.value, calcResult: null }))} className={inputCls}>
                    <option value="">{item.material_grade ? "Selecionar" : "Selecione a especificação primeiro"}</option>
                    {thicknessOptions.map((t) => <option key={t} value={t}>{t} mm</option>)}
                  </select>
                </div>
                {!sheetRequired && (
                  <>
                    <div>
                      <label className={labelCls}>Tratamento térmico</label>
                      <input data-testid="quote-heat-treatment-input" value={item.heat_treatment} onChange={setI("heat_treatment")} className={inputCls} placeholder="Ex.: têmpera, alívio de tensões" />
                    </div>
                    <div>
                      <label className={labelCls}>Dimensões da matéria-prima</label>
                      <input data-testid="quote-raw-dimensions-input" value={item.raw_dimensions} onChange={setI("raw_dimensions")} className={inputCls} placeholder="Ex.: barra Ø50 x 300 mm" />
                    </div>
                    <div>
                      <label className={labelCls}>Acabamento</label>
                      <input data-testid="quote-finish-input" value={item.finish} onChange={setI("finish")} className={inputCls} placeholder="Ex.: zincado, pintura, polido" />
                    </div>
                    <div>
                      <label className={labelCls}>Tolerâncias</label>
                      <input data-testid="quote-tolerances-input" value={item.tolerances} onChange={setI("tolerances")} className={inputCls} placeholder="Ex.: ±0,05 mm nas sedes" />
                    </div>
                  </>
                )}
                  </>
                )}
                <div className="sm:col-span-2">
                  <label className={labelCls}>Observações da peça</label>
                  <textarea data-testid="quote-notes-input" rows={3} value={item.notes} onChange={setI("notes")} className={`${inputCls} resize-none`} placeholder="Furos, roscas, aplicação da peça…" />
                </div>
              </div>
            )}

            {/* ARQUIVO DO ITEM */}
            {!isGrav && (
            <div className="mt-6" data-testid="item-files-section">
              <label className={labelCls}>
                {isJat ? "Fotos da peça — opcional (até 5)" : "Anexar arquivo da peça (DXF, DWG, STEP, PDF ou imagem)"}
              </label>
              <input
                ref={fileRef}
                data-testid="quote-files-input"
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const list = Array.from(e.target.files || []);
                  if (isJat && item.files.length + list.length > 5) {
                    toast.error("Máximo de 5 fotos por peça de jateamento.");
                    e.target.value = "";
                    return;
                  }
                  addItemFiles(list);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                data-testid="quote-add-files-button"
                onClick={() => fileRef.current?.click()}
                className="mt-2 w-full border border-dashed border-zinc-700 hover:border-orange-500 rounded-sm py-6 flex flex-col items-center gap-2 text-zinc-500 hover:text-orange-400 transition-colors"
              >
                <Paperclip className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-xs font-medium uppercase tracking-wider">Clique para anexar</span>
                <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">dxf · dwg · step · stp · pdf · jpg · png</span>
              </button>
              {analyzing && (
                <p className="mt-2 text-xs text-orange-400 flex items-center gap-2" data-testid="dxf-analyzing">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lendo geometria do DXF…
                </p>
              )}
              {!!item.files.length && (
                <ul className="mt-3 space-y-2" data-testid="quote-files-list">
                  {item.files.map((f, i) => (
                    <li key={`${f.name}-${i}`} data-testid={`quote-file-item-${i}`} className="flex items-center gap-3 rounded-sm border border-zinc-800 bg-zinc-900/50 px-3 py-2.5">
                      <FileText className="w-4 h-4 text-orange-500 shrink-0" />
                      <span className="text-sm text-zinc-300 truncate flex-1">{f.name}</span>
                      <span className="mono text-xs text-zinc-500">{fmtSize(f.size)}</span>
                      <button type="button" data-testid={`quote-file-remove-${i}`}
                        onClick={() => setItem((it) => ({ ...it, files: it.files.filter((_, j) => j !== i), analysis: it.files[i]?.name.toLowerCase().endsWith(".dxf") ? null : it.analysis, calcResult: null }))}
                        className="text-zinc-500 hover:text-red-400 transition-colors" aria-label={`Remover ${f.name}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {item.analysis && (
                <div className="mt-3 rounded-sm border border-emerald-900/50 bg-emerald-500/5 p-3 text-xs text-emerald-300" data-testid="dxf-analysis-summary">
                  DXF interpretado: <b>{Number(item.analysis.cut_length_m).toFixed(4)} m</b> de corte ·{" "}
                  <b>{item.analysis.pierce_count ?? 0}</b> entrada(s) · área <b>{Number(item.analysis.area_m2).toFixed(6)} m²</b>
                  {item.analysis.bbox ? ` · envelope ${item.analysis.bbox.width_mm} × ${item.analysis.bbox.height_mm} mm` : ""}
                </div>
              )}
            </div>
            )}
          </section>
        )}

        {/* ETAPA 4 — SERVIÇO ADICIONADO */}
        {step === 3 && (
          <section data-testid="quote-step-adicionado" className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" strokeWidth={1.25} />
            <h2 className="display mt-4 text-2xl font-bold uppercase">Serviço adicionado ao orçamento.</h2>
            <p className="mt-2 text-sm text-zinc-400">
              {items.length} serviço(s) no orçamento — os valores serão calculados e conferidos pela equipe.
            </p>
            <ul className="mt-5 max-w-md mx-auto space-y-2 text-left" data-testid="added-items-list">
              {items.map((it, i) => (
                <li key={i} data-testid={`added-item-${i}`} className="flex items-center justify-between gap-3 rounded-sm border border-zinc-800 bg-zinc-900/50 px-3.5 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-200 truncate">{i + 1}. {it.service}{it.part_name ? ` — ${it.part_name}` : ""}</div>
                    <div className="text-[11px] text-zinc-500">
                      {it.quantity} un.
                      {it.material ? ` · ${it.material}` : ""}
                      {it.thickness_mm ? ` ${it.thickness_mm} mm` : ""}
                      {it.service === DOBRA_SERVICE && it.dobra?.material ? ` · ${it.dobra.material} ${it.dobra.thickness_mm} mm · ${it.dobra.bend_count} dobra(s)` : ""}
                      {it.service === GRAV_SERVICE && it.gravacao ? ` · ${it.gravacao.machine} · ${it.gravacao.width_mm}×${it.gravacao.height_mm} mm` : ""}
                      {USINAGEM_SERVICES.has(it.service) && it.usinagem?.material ? ` · ${it.usinagem.material}` : ""}
                    </div>
                  </div>
                  {!!it.files.length && <span className="text-[11px] text-zinc-500 shrink-0">{it.files.length} arquivo(s)</span>}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                data-testid="add-another-service-button"
                onClick={addAnother}
                className="inline-flex items-center gap-2 border border-orange-600 text-orange-400 hover:bg-orange-500/10 text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
              >
                <Plus className="w-4 h-4" /> Adicionar outro serviço
              </button>
              <button
                type="button"
                data-testid="go-review-button"
                onClick={() => { setStep(4); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
              >
                Enviar orçamento <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        )}

        {/* ETAPA 5 — REVISÃO FINAL */}
        {step === 4 && (
          <section data-testid="quote-step-revisao">
            <h2 className="display text-xl font-bold uppercase tracking-wide flex items-center gap-2">
              <span className="mono text-orange-500 text-sm">05</span> Revisão final
            </h2>

            <div className="mt-5 rounded-sm border border-zinc-800 bg-zinc-900/40 p-4" data-testid="review-client">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Cliente</div>
              <div className="text-sm text-zinc-200 font-medium">{client.client_name}</div>
              <div className="mt-1 text-xs text-zinc-400 leading-relaxed">
                {client.client_document} · {client.client_email} · {client.client_phone}<br />
                {[client.address, client.address_number, client.complement].filter(Boolean).join(", ")}
                {client.neighborhood ? ` — ${client.neighborhood}` : ""}
                {client.cep ? ` · CEP ${client.cep}` : ""} · {client.city}/{client.state}
              </div>
              <button type="button" data-testid="review-edit-client" onClick={() => setStep(0)}
                className="mt-2 text-[11px] uppercase tracking-wider text-orange-400 hover:text-orange-300">
                Editar dados do cliente
              </button>
            </div>

            <div className="mt-5 space-y-3" data-testid="review-items">
              {items.map((it, i) => (
                <div key={i} className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`review-item-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-widest text-zinc-500">Item {i + 1}</div>
                      <div className="mt-0.5 text-sm font-semibold text-zinc-100">{it.service}</div>
                      <div className="mt-1 text-xs text-zinc-400 leading-relaxed">
                        {it.part_name ? <>Peça: <b className="text-zinc-300">{it.part_name}</b> · </> : null}
                        Quantidade: <b className="text-zinc-300">{it.quantity}</b>
                        {it.material ? <> · {it.material}{it.material_grade && it.material_grade !== GRADE_PADRAO ? ` ${it.material_grade}` : ""}{it.thickness_mm ? ` ${it.thickness_mm} mm` : ""}</> : null}
                        {it.service === JAT_SERVICE ? <> · {it.jateamento.faces} faces · dificuldade {it.jateamento.difficulty}</> : null}
                        {it.service === DOBRA_SERVICE ? <> · {it.dobra.material} {it.dobra.thickness_mm} mm · dobra {it.dobra.bend_length_mm} mm × {it.dobra.bend_count}</> : null}
                        {it.service === GRAV_SERVICE && it.gravacao ? <> · {it.gravacao.machine} · {it.gravacao.depth} · {it.gravacao.width_mm}×{it.gravacao.height_mm} mm</> : null}
                        {USINAGEM_SERVICES.has(it.service) && it.usinagem ? <> · {it.usinagem.material} · {({ cilindrica: "Cilíndrica", quadrada: "Quadrada", retangular: "Retangular" })[it.usinagem.format] || ""}</> : null}
                        {it.notes ? <><br />Obs: {it.notes}</> : null}
                        {!!it.files.length && <><br />{it.files.length} arquivo(s): {it.files.map((f) => f.name).join(", ")}</>}
                        {!it.files.length && <><br /><span className="text-amber-400/90">Sem arquivo anexado (rascunho restaurado? reanexe ao editar o item)</span></>}
                      </div>
                      {it.techFlag && (
                        <div className="mt-2 text-[11px] font-bold uppercase tracking-wider text-red-400" data-testid={`review-item-tech-${i}`}>
                          Necessita análise técnica — a equipe fará a conferência manual deste item
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="mt-1 flex gap-1.5 justify-end">
                        <button type="button" data-testid={`review-item-edit-${i}`} onClick={() => editItem(i)}
                          className="inline-flex items-center gap-1 border border-zinc-700 hover:border-orange-500 text-zinc-400 hover:text-orange-400 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-sm transition-colors">
                          <Pencil className="w-3 h-3" /> Editar
                        </button>
                        <button type="button" data-testid={`review-item-remove-${i}`} onClick={() => removeItem(i)}
                          className="inline-flex items-center gap-1 border border-zinc-700 hover:border-red-500 text-zinc-400 hover:text-red-400 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-sm transition-colors">
                          <Trash2 className="w-3 h-3" /> Excluir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" data-testid="review-add-service" onClick={addAnother}
                className="w-full border border-dashed border-zinc-700 hover:border-orange-500 rounded-sm py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-orange-400 transition-colors">
                + Adicionar outro serviço
              </button>
            </div>

            <div className="mt-5 rounded-sm border border-zinc-800 bg-zinc-900/30 p-4 text-xs text-zinc-500 leading-relaxed" data-testid="review-total">
              Os valores do orçamento são calculados pela equipe e apresentados somente após a conferência.
              Você será avisado por e-mail quando o orçamento final estiver disponível.
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Prazo desejado</label>
                <input data-testid="quote-deadline-input" type="date" value={extras.deadline}
                  onChange={(e) => setExtras({ ...extras, deadline: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Urgência</label>
                <select data-testid="quote-urgency-select" value={extras.urgency}
                  onChange={(e) => setExtras({ ...extras, urgency: e.target.value })} className={inputCls}>
                  <option>Normal</option>
                  <option>Urgente</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Observações gerais</label>
                <textarea data-testid="quote-general-notes-input" rows={3} value={extras.general_notes}
                  onChange={(e) => setExtras({ ...extras, general_notes: e.target.value })}
                  className={`${inputCls} resize-none`} placeholder="Condições de entrega, embalagem, certificações necessárias…" />
              </div>
            </div>

            <div className="mt-5 rounded-sm border border-zinc-800 bg-zinc-900/30 p-4" data-testid="review-default-obs">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Observações para o cliente (padrão do orçamento)</div>
              <p className="text-[11px] text-zinc-500 whitespace-pre-line leading-relaxed">{DEFAULT_OBS}</p>
              <p className="mt-2 text-[10px] text-zinc-600">Validade do orçamento: 3 dias · Prazo de produção: 5 dias (ajustáveis pela equipe).</p>
            </div>
          </section>
        )}

        {/* NAV */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="quote-step-back"
            onClick={back}
            disabled={step === 0 || step === 3}
            className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 text-sm font-semibold uppercase tracking-wider px-5 py-3 rounded-sm transition-colors hover:border-zinc-500 disabled:opacity-40 disabled:pointer-events-none"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          {step === 0 || step === 1 ? (
            <button
              type="button"
              data-testid="quote-step-next"
              onClick={next}
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
            >
              Próximo <ArrowRight className="w-4 h-4" />
            </button>
          ) : step === 2 ? (
            <button
              type="button"
              data-testid="item-advance-button"
              onClick={advanceItem}
              disabled={calculating || analyzing}
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
            >
              {calculating || analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {editIdx != null ? "Salvar alterações" : "Avançar"}
            </button>
          ) : step === 4 ? (
            <button
              type="button"
              data-testid="quote-submit-button"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-bold uppercase tracking-widest px-7 py-3.5 rounded-sm transition-colors"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Enviar orçamento
            </button>
          ) : (
            <span />
          )}
        </div>
      </div>

      {jatHelp && createPortal(
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75" onClick={() => setJatHelp(false)} data-testid="jat-diff-help-modal">
          <div className="card-tactical rounded-md p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="display text-base font-bold uppercase tracking-wide">Veja qual classificação sua peça pertence</h3>
              <button type="button" onClick={() => setJatHelp(false)} data-testid="jat-diff-help-close"
                className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 border border-zinc-700 rounded-sm px-2.5 py-1.5 transition-colors">
                Fechar
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {JAT_DIFF_HELP.map((d) => (
                <div key={d.key} className="rounded-sm border border-zinc-800 bg-zinc-900/50 p-3.5" data-testid={`jat-help-${d.key}`}>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${d.dot}`} />
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-200">{d.title}</span>
                  </div>
                  <p className="mt-1.5 text-xs text-zinc-400 leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-3">
              Em caso de dúvida, selecione a opção que mais se aproxima da dificuldade da peça. A classificação poderá ser ajustada após análise das fotos.
            </p>
            <button type="button" onClick={() => setJatHelp(false)}
              className="mt-4 w-full pill-accent rounded-md px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition">
              Entendi
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
